import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Box, palette, useHover } from '@landr/maestro';
import { CirclePlayer } from '@landr/react-player';
import { Pack, RePack } from 'types';
import { useTrackingContext } from 'contexts/TrackingContext/TrackingContext';
import { PackPlayButtonClickTrackerV2Type } from 'contexts/TrackingContext/TrackingContext.types';
import { InteractionSource } from 'utils/analytics/AnalyticsV2Service.generated';
import { usePreviewContext } from 'components/Carousels/contexts/PreviewContext/PreviewContext';
import { usePlayerContext } from 'contexts/PlayerContext/PlayerContext';

export const PlayerWrapper = styled(Box)<{ isVisible: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    opacity: 0;
    transition: opacity 250ms;

    background-color: ${palette('neutral.1000', 0.32)};

    :focus-visible {
        outline: none;
    }

    ${({ isVisible }) =>
        isVisible &&
        `
        opacity: 1;  
    `};
`;

const CustomCirclePlayerConfig = {
    buttonColor: 'rgba(255, 255, 255, 0.7)',
    hoverButtonColor: 'rgba(255, 255, 255, 1)',
    iconColor: 'rgb(20, 39, 57)',
};

export const _CircleDemoPlayer = ({
    guid,
    handlePlay,
    handlePause,
}: {
    guid: string;
    handlePlay: () => void;
    handlePause: () => void;
}) => (
    <CirclePlayer
        playerId={guid}
        tracksCount={1}
        onPlayButtonClick={handlePlay}
        onPauseButtonClick={handlePause}
        themeConfig={CustomCirclePlayerConfig}
    />
);

export function useCircleDemoPlayer({
    togglePlaying,
    getPackPlayButtonClickTrackerV2,
    entity,
    totalTime,
}: {
    togglePlaying: () => void;
    getPackPlayButtonClickTrackerV2: PackPlayButtonClickTrackerV2Type;
    entity: Pack | RePack;
    totalTime: number;
}) {
    const { contentGroup, contentList } = usePreviewContext();

    const handlePlay = useCallback(() => {
        togglePlaying();
        getPackPlayButtonClickTrackerV2({
            interactionSource: InteractionSource.CardAction,
            item: entity,
            contentGroup,
            contentList,
        })(totalTime);
    }, [
        togglePlaying,
        getPackPlayButtonClickTrackerV2,
        entity,
        contentGroup,
        contentList,
        totalTime,
    ]);

    return { handlePlay };
}

export type CircleDemoPlayerProps = {
    entity: Pack | RePack;
    guid: string;
};

const CircleDemoPlayer = ({ entity, guid }: CircleDemoPlayerProps) => {
    const [hoverRef, isHovered] = useHover(false);
    const { getPackPlayButtonClickTrackerV2 } = useTrackingContext();

    const { togglePlaying, playing, totalTime } = usePlayerContext();

    const { handlePlay } = useCircleDemoPlayer({
        togglePlaying,
        getPackPlayButtonClickTrackerV2,
        entity: entity,
        totalTime,
    });

    return (
        <PlayerWrapper
            tabIndex={-1}
            ref={hoverRef}
            isVisible={playing || isHovered}
        >
            <_CircleDemoPlayer
                guid={guid}
                handlePlay={handlePlay}
                handlePause={togglePlaying}
            />
        </PlayerWrapper>
    );
};

export default CircleDemoPlayer;
